// ── Provider cache ────────────────────────────────────────────────────────────
// Module-level Map so provider data persists across renders without re-fetching.
// Fetches US flatrate+free+ads providers from TMDB on demand, silently.
//
// SEASON TRUTH (17.164, Jason 2026-11-01): TMDB's show-level list is the UNION of
// every season's carriers. Skinwalker Ranch listed Netflix because Netflix has the
// early seasons; the season he was about to watch (S7) is on Philo only, and the
// deck's pill said "Netflix". Every "watch on" surface now asks for the season of
// the NEXT episode first (`/tv/{id}/season/{n}/watch/providers`) and only falls
// back to the show-level union when TMDB has nothing for that season.

// tmdb reads import.meta.env at module load, so it is imported lazily — this
// module's pure helper is node-tested (see ship-gate-grep-trap in memory).
const tmdb = () => import('./tmdb')

const cache   = new Map()   // key → provider id array
const pending = new Map()   // key → Promise (prevents duplicate in-flight fetches)

// ── Day-scale persistence (17.170) ──────────────────────────────────────────
// tmdbGet remembers a response for 20 minutes per tab. Carriers change on a scale
// of days, and the "Where it's playing" sheet asks for one region per season (a
// 20-season show = 20 calls each time it opens in a new tab). One localStorage
// blob, 24h TTL, newest-400 entries, loaded once per page. Pure: the store takes
// its storage and clock so it is node-tested without a browser.
const PERSIST_KEY = 'nextep_providers_v1'
export const PERSIST_TTL = 24 * 60 * 60 * 1000
export const PERSIST_MAX = 400
export class ProviderStore {
  constructor(storage, now = () => Date.now()) {
    this.storage = storage; this.now = now; this.map = null
  }
  load() {
    if (this.map) return this.map
    this.map = new Map()
    try {
      const raw = this.storage?.getItem(PERSIST_KEY)
      const obj = raw ? JSON.parse(raw) : {}
      for (const [k, e] of Object.entries(obj)) if (e && typeof e.t === 'number') this.map.set(k, e)
    } catch { /* unreadable blob: start clean */ }
    return this.map
  }
  get(key) {
    const e = this.load().get(key)
    if (!e) return undefined
    if (this.now() - e.t > PERSIST_TTL) { this.map.delete(key); return undefined }
    return e.v
  }
  set(key, v) {
    const m = this.load()
    m.delete(key); m.set(key, { t: this.now(), v })   // re-insert = newest
    while (m.size > PERSIST_MAX) m.delete(m.keys().next().value)   // Map keeps insertion order: oldest first
    try { this.storage?.setItem(PERSIST_KEY, JSON.stringify(Object.fromEntries(m))) } catch { /* quota / private mode */ }
  }
  clear() { this.map = new Map(); try { this.storage?.removeItem(PERSIST_KEY) } catch {} }
}
let _store = null
const store = () => _store || (_store = new ProviderStore(typeof localStorage !== 'undefined' ? localStorage : null))

// Pure: the US streaming ids (subscription, free, ad-supported) in a TMDB
// watch/providers payload. Rent/buy are deliberately NOT "watch on" options.
export function streamIds(data, region = 'US') {
  const r = data?.results?.[region] || {}
  const ids = [...(r.flatrate || []), ...(r.free || []), ...(r.ads || [])].map(p => p.provider_id)
  return [...new Set(ids)]
}

function fetchIds(key, path) {
  if (cache.has(key)) return Promise.resolve(cache.get(key))
  if (pending.has(key)) return pending.get(key)
  // An EMPTY list is not remembered for the day: a season TMDB hasn't mapped yet is
  // usually mapped within hours, and pinning [] would keep the union fallback a day.
  const kept = store().get('ids:' + key)
  if (kept && kept.length) { cache.set(key, kept); return Promise.resolve(kept) }
  const p = tmdb().then(m => m.tmdbGet(path))
    .then(data => { const ids = streamIds(data); cache.set(key, ids); if (ids.length) store().set('ids:' + key, ids); pending.delete(key); return ids })
    .catch(() => { pending.delete(key); return null })
  pending.set(key, p)
  return p
}

export async function getProviders(showId) {
  return (await fetchIds(String(showId), `/tv/${showId}/watch/providers`)) || []
}

// The carriers of ONE season, falling back to the show-level union when TMDB lists
// nothing for that season (new seasons are often unmapped for a few days). Pass
// the season of the episode the person is about to watch.
export async function getProvidersFor(showId, season) {
  const n = Number(season)
  if (!showId || !Number.isFinite(n) || n < 1) return getProviders(showId)
  const ids = await fetchIds(`${showId}/s${n}`, `/tv/${showId}/season/${n}/watch/providers`)
  if (ids && ids.length) return ids
  return getProviders(showId)
}

// Test seam.
export function _resetProviderCache() { cache.clear(); pending.clear(); regions.clear(); store().clear() }

// The raw US region payload for one season (flatrate/free/ads/rent/buy/link),
// cached — the "Where it's playing" sheet draws one row per season from these.
const regions = new Map()
function fetchRegion(key, path, region) {
  if (regions.has(key)) return regions.get(key)
  const kept = store().get('region:' + key)
  if (kept !== undefined) { const p = Promise.resolve(kept); regions.set(key, p); return p }
  const p = tmdb().then(m => m.tmdbGet(path))
    .then(d => { const r = d?.results?.[region] || null; store().set('region:' + key, r); return r })
    .catch(() => { regions.delete(key); return null })   // a failed call is not remembered
  regions.set(key, p)
  return p
}
export async function getSeasonRegion(showId, season, region = 'US') {
  return fetchRegion(`${showId}/s${season}/${region}`, `/tv/${showId}/season/${season}/watch/providers`, region)
}
export async function getShowRegion(showId, region = 'US') {
  return fetchRegion(`${showId}/show/${region}`, `/tv/${showId}/watch/providers`, region)
}
